import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import classNames from 'classnames';
import { withRouter } from 'react-router';
import queryString from 'query-string';
import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';

import { LISTING_STATUS_ACTIVE } from 'common/listingConstants';

import {
  getShowingIsRequested,
  getShowingIsScheduled,
  isSelfShowingListing,
} from 'common/listingHelpers';

import { isPreLeaseActive } from 'common/preLeaseHelpers';

import { hasRoleAnonymous } from 'common/userRoles';

import {
  hasUserAlreadyScheduledATour,
  getUpcomingTours,
  isLockedTour,
  shouldSendTourRequestEmail,
} from 'common/tourHelpers';

import {
  SR_API_ACTION_REQUEST_TOUR_SHOWING,
  SR_API_ACTION_GET_TOUR_LIST,
  SR_API_ACTION_TOUR_JOIN_WAITLIST,
} from 'api/actionTypes';

import TourActions from 'client/actions/Tour';
import branch from 'client/core/branch';
import withApiState from 'client/helpers/withApiState';
import postWithState from 'client/actions/postWithState';
import withLocationQuery from 'client/helpers/withLocationQuery';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrButton, { SR_BUTTON_THEME_ADOBE_GREY } from 'client/components/SrButton/SrButton';
import SrModalV2, { MODAL_THEME_STACK } from 'client/components/SrModalV2/SrModalV2';
import SrDialogBox from 'client/components/SrDialogBox';
import ButtonDeprecated from 'client/components/ButtonDeprecated';
import SrSimpleMessageModal from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';

@withRouter
@withLocationQuery
@withApiState(({ listing }) => {
  return {
    requestShowingApiState: {
      action: SR_API_ACTION_REQUEST_TOUR_SHOWING,
      queryId: listing.id,
    },
    tourListApiState: {
      action: SR_API_ACTION_GET_TOUR_LIST,
      queryId: listing.id,
    },
    joinWaitlistApiState: {
      action: SR_API_ACTION_TOUR_JOIN_WAITLIST,
      queryId: String(listing.id),
    },
  };
})
@branch({
  currentUser: ['currentUser'],
  tours: ['tours'],
})
class SrRequestShowingButton extends Component {
  static propTypes = {
    currentUser: PropTypes.object.isRequired,
    tours: PropTypes.array,
    listing: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    requestShowingApiState: PropTypes.object.isRequired,
    tourListApiState: PropTypes.object.isRequired,
    selectedDate: PropTypes.string,
    hasRecentTourRequest: PropTypes.bool,
    isRecommendation: PropTypes.bool,
    requestedFrom: PropTypes.string,
    buttonTextOverride: PropTypes.string,
    location: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);
    const locationQuery = queryString.parse(props.location.search);
    this.state = {
      showDialogBox: false,
      showLimitWarningModal: false,
      isWaitlistSuccessModalOpen: false,
      tourRequestEmailSent: locationQuery.tourRequestEmailSent === 'true',
    };
  }

  componentDidMount() {
    const { locationQuery } = this.props;

    if (locationQuery.source === 'joinWailist') {
      this._onJoinWaitlist();
    }
  }

  _onScheduleTourClick = () => {
    const {
      listing,
      dispatch,
      selectedDate,
      tours,
      requestedFrom,
      currentUser,
      history,
    } = this.props;

    if (isSelfShowingListing(listing)) {
      history.push(`/listing/${listing.id}?showSelfShowingSteps=true`);
      return;
    }

    const upcomingTours = getUpcomingTours(tours).filter(
      (tour) => tour.startDate || tour.markedNeedsReschedulingFlag,
    );

    if (shouldSendTourRequestEmail({ currentUser, listing })) {
      dispatch(TourActions.sendTourRequestEmail, {
        listingId: listing.id,
      });
      this.setState({
        tourRequestEmailSent: true,
      });
    } else {
      // If there are already 5, show limit warning modal
      if (upcomingTours.length > 0 && upcomingTours[0].listings?.length >= 5) {
        this.setState({ showLimitWarningModal: true });
        return;
      }

      const requestShowingPayload = {
        listingId: listing.id,
        locationQuery: {
          requestedFrom: requestedFrom || null,
          date: selectedDate,
          addToTourId: upcomingTours.length > 0 ? upcomingTours[0].id : null,
          needsRescheduling:
            upcomingTours.length > 0 && upcomingTours[0].markedNeedsReschedulingFlag,
        },
      };

      if (upcomingTours.length > 0) {
        isLockedTour({
          tour: upcomingTours[0],
          onUnlocked: () => {
            dispatch(TourActions.requestShowingStepSubmit, requestShowingPayload);
          },
        });
      } else {
        dispatch(TourActions.requestShowingStepSubmit, requestShowingPayload);
      }
    }
  };

  _onCreateNewTour = () => {
    const { history, listing } = this.props;
    history.push(`/listing/${listing.id}/choose-tour-time?createNewTour=true`);
  };

  _onJoinWaitlist = () => {
    const { listing, history, dispatch, currentUser } = this.props;

    if (hasRoleAnonymous(currentUser.roles)) {
      history.push(
        `/login?${queryString.stringify({
          joinWaitlist: true,
          listingId: listing.id,
        })}`,
      );
      return;
    }

    dispatch(postWithState, {
      action: SR_API_ACTION_TOUR_JOIN_WAITLIST,
      queryId: String(listing.id),
      payload: {
        listingId: listing.id,
      },
      onSuccess: () => {
        this.setState({
          isWaitlistSuccessModalOpen: true,
        });
      },
    });
  };

  _onCloseWaitlistSuccessModal = () => {
    this.setState({
      isWaitlistSuccessModalOpen: false,
    });
  };

  _shouldDisplayWaitlist = () => {
    const { preLease } = this.props;
    return preLease && isPreLeaseActive({ preLease });
  };

  _getButtonText = () => {
    const {
      isRecommendation,
      tours,
      listing,
      requestShowingApiState,
      tourListApiState,
      joinWaitlistApiState,
      buttonTextOverride,
      hasRecentTourRequest,
    } = this.props;

    const { tourRequestEmailSent } = this.state;

    const shouldDisplayWaitlist = this._shouldDisplayWaitlist();
    const scheduledTourAlready = hasUserAlreadyScheduledATour({ tours });
    const isShowingRequested = getShowingIsRequested({ listing, tours });
    const isShowingScheduled = getShowingIsScheduled({ listing, tours });
    const isNotActive = listing.status !== LISTING_STATUS_ACTIVE;
    const isLoading =
      requestShowingApiState.inProgress ||
      tourListApiState.inProgress ||
      joinWaitlistApiState.inProgress;
    if (tourRequestEmailSent || hasRecentTourRequest) {
      return 'tour request sent!';
    }
    if (isShowingScheduled) {
      return 'scheduled';
    }
    if (isShowingRequested) {
      return isRecommendation ? 'added!' : 'tour requested';
    }
    if (isNotActive) {
      return 'not available';
    }
    if (isLoading) {
      return 'loading...';
    }
    if (shouldDisplayWaitlist) {
      return 'join waitlist';
    }
    if (scheduledTourAlready) {
      return isRecommendation ? 'add to tour' : 'add home to tour';
    }
    if (isSelfShowingListing(listing)) {
      return 'visit this home';
    }
    if (buttonTextOverride) {
      return buttonTextOverride;
    }
    return 'see this home';
  };

  _isButtonDisabled = () => {
    const {
      tours,
      listing,
      requestShowingApiState,
      tourListApiState,
      joinWaitlistApiState,
      hasRecentTourRequest,
    } = this.props;

    const { tourRequestEmailSent } = this.state;

    const isShowingRequested = getShowingIsRequested({ listing, tours });
    const isShowingScheduled = getShowingIsScheduled({ listing, tours });
    const isNotActive = listing.status !== LISTING_STATUS_ACTIVE;
    const isLoading =
      requestShowingApiState.inProgress ||
      tourListApiState.inProgress ||
      joinWaitlistApiState.inProgress;

    return (
      isShowingRequested ||
      isShowingScheduled ||
      isNotActive ||
      isLoading ||
      tourRequestEmailSent ||
      hasRecentTourRequest
    );
  };

  _getWarningMessage = () => {
    const { tours } = this.props;

    const upcomingTour = getUpcomingTours(tours)[0];

    let limitWarningMessage =
      'We’re happy you want to tour so many homes, but Sunroom has a limit of ' +
      '5 homes per tour. Please wait to add more homes until you complete the tour';

    if (upcomingTour?.startDate) {
      const timeFormatted = momentWithAustinTimezone(upcomingTour.startDate).calendar(null, {
        sameDay: '[today] [at] h:mmA',
        nextDay: '[tomorrow] [at] h:mmA',
        nextWeek: 'dddd [at] h:mmA',
      });
      limitWarningMessage += ` you have scheduled for ${timeFormatted}.`;
    } else {
      limitWarningMessage += ' you have already requested.';
    }

    return limitWarningMessage;
  };

  _handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const shouldDisplayWaitlist = this._shouldDisplayWaitlist();

    if (shouldDisplayWaitlist) {
      return this._onJoinWaitlist();
    }
    return this._onScheduleTourClick();
  };

  render() {
    const {
      isRecommendation,
      tours,
      listing,
      preLease,
      requestShowingApiState,
      tourListApiState,
      joinWaitlistApiState,
    } = this.props;

    const { showDialogBox, showLimitWarningModal, isWaitlistSuccessModalOpen } = this.state;

    const upcomingTour = getUpcomingTours(tours)[0];

    const isShowingRequested = getShowingIsRequested({ listing, tours });
    const isLoading =
      requestShowingApiState.inProgress ||
      tourListApiState.inProgress ||
      joinWaitlistApiState.inProgress;

    const buttonText = this._getButtonText();
    const isDisabled = this._isButtonDisabled();
    const limitWarningMessage = this._getWarningMessage();

    if (listing.cantTourFlag) {
      return null;
    }

    return (
      <div>
        <ButtonDeprecated
          disabled={isDisabled}
          onClick={this._handleClick}
          className={classNames({ recommendation: isRecommendation })}
        >
          <span className={classNames({ loading: isLoading }, { added: isShowingRequested })}>
            {buttonText}
          </span>
        </ButtonDeprecated>

        {showDialogBox && (
          <SrDialogBox
            isOpen
            title="Create a new tour"
            message={`This apartment is closed during your tour on ${momentWithAustinTimezone(
              upcomingTour.startDate,
            ).format('dddd')}. Do you want to tour this apartment another day?`}
            actions={[
              {
                id: 'cancel',
                text: 'Cancel',
                onClick: () => this.setState({ showDialogBox: false }),
              },
              {
                id: 'yes',
                text: 'Yes',
                onClick: this._onCreateNewTour,
              },
            ]}
            onClose={() => this.setState({ showDialogBox: false })}
          />
        )}
        {showLimitWarningModal && (
          <SrSimpleMessageModal
            iconUrl="/images/house-illustration.png"
            headline="You’ve reached the maximum homes on a tour"
            message={limitWarningMessage}
            buttonText="ok, i understand"
            maxWidth={375}
            maxIconWidth={200}
            onClose={() => this.setState({ showLimitWarningModal: false })}
          />
        )}
        {isWaitlistSuccessModalOpen && (
          <SrModalV2
            showCloseButton={false}
            closeOnBackgroundClick
            contentPadding="24px 22px 34px"
            contentMaxWidth="414px"
            theme={MODAL_THEME_STACK}
            onClose={this._onCloseWaitlistSuccessModal}
          >
            <SrText
              font="greycliff"
              weight="800"
              size="26"
              lineHeight="36px"
              letterSpacing="0.5px"
              align="center"
              width="100%"
              display="block"
            >
              Be the First to Tour!
            </SrText>
            <SrBox textAlign="center">
              <img alt="header underline" src="/images/yellow-underline.svg" />
            </SrBox>
            <SrBox textAlign="center" margin="28px 20px 0">
              <SrText
                font="greycliff"
                size="18"
                weight="600"
                lineHeight="28px"
                letterSpacing="0.5px"
              >
                {'This home will be ready for tours as soon as '}
                <SrText
                  font="greycliff"
                  size="18"
                  weight="800"
                  lineHeight="28px"
                  letterSpacing="0.5px"
                >
                  {moment(preLease.startDate).format('MMM D')}.
                </SrText>
                {
                  " We will notify you as soon as it's available, so you can be one of the first to visit!"
                }
              </SrText>
            </SrBox>
            <SrBox marginTop="33px">
              <SrButton
                theme={SR_BUTTON_THEME_ADOBE_GREY}
                onClick={this._onCloseWaitlistSuccessModal}
              >
                Close
              </SrButton>
            </SrBox>
          </SrModalV2>
        )}
      </div>
    );
  }
}

SrRequestShowingButton.displayName = 'SrRequestShowingButton';

export default SrRequestShowingButton;
